import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CreateInvestment2 from "./Forms/createInvestment2";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import { GoBriefcase } from "react-icons/go";
import { ReactComponent as RightArrow } from "../../../myfin_v2/assets/icons/rightArrowEcl.svg";
import { ReactComponent as ArrowRightIcon } from "../../../myfin_v2/assets/icons/arrowRight.svg";
import { ReactComponent as CalendarIcon } from "../../../myfin_v2/assets/icons/calender.svg";
const statusStyles = {
  Investment: { bg: "#E8FEFF", color: "#42778B", border: "#CAE6E6" },
  Associate: { bg: "#FDF5E9", color: "#A7683E", border: "#EDE3D8" },
  "Associate Company": { bg: "#FDF5E9", color: "#A7683E", border: "#EDE3D8" },
  Subsidiary: { bg: "#F0EBFD", color: "#564AC5", border: "#D4D1E3" },
  "Joint Venture": { bg: "#F6FDE7", color: "#778F41", border: "#DAEAAE" },
};

const dotStyles = {
  Investment: { outerBg: "#E8FEFF", outerBorder: "#CAE6E6", innerBg: "#42778B" },
  Associate: { outerBg: "#FDF5E9", outerBorder: "#EDE3D8", innerBg: "#A7683E" },
  "Associate Company": { outerBg: "#FDF5E9", outerBorder: "#EDE3D8", innerBg: "#A7683E" },
  Subsidiary: { outerBg: "#F0EBFD", outerBorder: "#D4D1E3", innerBg: "#564AC5" },
  "Joint Venture": { outerBg: "#F6FDE7", outerBorder: "#DAEAAE", innerBg: "#778F41" },
};

const getStatusLabel = (status) => {
  if (!status) return "Investment";

  const normalized = status.trim().toLowerCase();

  if (normalized === "investment") return "Investment";
  if (normalized === "associate company" || normalized === "associate")
    return "Associate";
  if (normalized === "subsidiary") return "Subsidiary";
  if (normalized === "joint venture") return "Joint Venture";

  return "Investment";
};
const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getStatusByOwnership = (ownershipPercent) => {
  const percent = Number(ownershipPercent);
  if (percent > 50) return "Subsidiary";
  if (percent === 50) return "Joint Venture";
  if (percent > 20 && percent < 50) return "Associate";
  return "Investment";
};

const InvestmentTimeline = ({ timelineData,  investmentID, sortOrder, onRefresh  }) => {
  const [timeline, setTimeline] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("Investment");
  const [openEntry, setOpenEntry] = useState(null);
  const [entryData, setEntryData] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const handleOpen = (entry) => {
    setEntryData({
      ...entry.initialData,
      totalSharesOutstanding: entry.initialData?.totalSharesOutstanding || entry.totalSharesOutstanding,
      previousShares: entry.sharesFrom ?? 0,
      previousOwnership: Number(entry.ownership),
    });

    setOpenEntry(entry);
  };

  useEffect(() => {
    if (!timelineData?.timeline) return;

    const sorted = [...timelineData.timeline].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : (a.transactionDate ? new Date(a.transactionDate) : 0);
      const dateB = b.createdAt ? new Date(b.createdAt) : (b.transactionDate ? new Date(b.transactionDate) : 0);
      return dateA - dateB;
    });

    let prevShares = 0;
    let runningTotalSharesOutstanding = 0;

    const formatted = sorted.map((item, i) => {
      const purchased = Number(item.noOfSharesPurchased) || 0;
      const sold = Number(item.noOfSharesSold) || 0;
      const boughtBack = Number(item.noOfSharesBoughtBack) || 0;
      const bonus = Number(item.bonusSharesReceived) || 0;
      const rights = Number(item.rightsExercised) || 0;

      if (item.totalSharesOutstanding) {
        runningTotalSharesOutstanding = Number(item.totalSharesOutstanding);
      }

      const nature = (item.natureOfTransaction || "").toLowerCase();
      const isSale = nature.includes("sale");
      const isBuyback = nature.includes("buyback");
      const isBonus = nature.includes("bonus");
      const isRights = nature.includes("rights");

      let newShares = prevShares;
      if (isSale) {
        newShares = Math.max(0, prevShares - sold);
      } else if (isBuyback) {
        newShares = Math.max(0, prevShares - boughtBack);
      } else if (isBonus) {
        newShares = prevShares + bonus;
      } else if (isRights) {
        newShares = prevShares + rights;
      } else {
        newShares = prevShares + purchased;
      }

      const ownershipVal = runningTotalSharesOutstanding > 0
        ? ((newShares / runningTotalSharesOutstanding) * 100).toFixed(2)
        : (item.natureOfRelationship ? (item.natureOfRelationship === "Subsidiary" ? "51.00" : "0.00") : "0.00");

      const status = runningTotalSharesOutstanding > 0
        ? getStatusByOwnership(ownershipVal)
        : (item.natureOfRelationship || getStatusByOwnership(ownershipVal));

      const entry = {
        id: item._id,
        investmentID: investmentID,
        initialData: item,
        date: formatDate(item.transactionDate),
        title: item.natureOfTransaction || "Purchase of Shares",
        tranch: `TRANCH ${i + 1}`,
        isSale,
        isBuyback,
        isBonus,
        isRights,
        noOfSharesPurchased: purchased,
        noOfSharesSold: sold,
        noOfSharesBoughtBack: boughtBack,
        bonusSharesReceived: bonus,
        rightsExercised: rights,
        salePricePerShare: Number(item.salePricePerShare) || 0,
        totalSaleProceeds: Number(item.totalSaleProceeds) || 0,
        costOfSharesSold: Number(item.costOfSharesSold) || 0,
        gainOrLossInSale: Number(item.gainOrLossInSale ?? item.gainOrLoss) || 0,
        costBasisMethod: item.costBasisMethod || "",
        soldTo: item.soldTo || "",
        totalConsideration: Number(item.totalConsideration) || 0,
        purchasePricePerShare: Number(item.purchasePricePerShare) || 0,
        totalBuybackProceeds: Number(item.totalBuybackProceeds) || 0,
        amountPaid: Number(item.amountPaid) || 0,
        splitRatio: item.splitRatio || "",
        sharesFrom: i === 0 ? null : prevShares,
        sharesTo: newShares,
        ownership: ownershipVal,
        status: status,
        totalSharesOutstanding: runningTotalSharesOutstanding,
      };

      prevShares = newShares;
      return entry;
    });

    const finalTimeline =
      sortOrder === "desc" ? [...formatted].reverse() : formatted;

    setTimeline(finalTimeline);

    const latestEntry = formatted.length ? formatted[formatted.length - 1] : null;
    setCurrentStatus(
      latestEntry ? getStatusLabel(latestEntry.status) : "Investment"
    );

  }, [timelineData, sortOrder, investmentID]);

  const handleBack = () => {
    setOpenEntry(null);
    setEntryData(null);
    if (onRefresh) onRefresh();
  };

  return (
    <div style={{ position: "relative" }}>
    <div style={{ background: "#fff", padding: 32, borderRadius: 16 }}>
      <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 30,
        paddingBottom: 16,
        borderBottom: "1px solid #eaeeed",
        marginLeft: -32,  
        marginRight: -32,
        paddingLeft: 32,
        paddingRight: 32,
      }}
    >
        <h6 style={{ fontWeight: "500" , fontSize: "20px" }}>Investment Timeline</h6>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: -10 }}>
          <span style={{ fontSize: "14px", color: "#66827A" }}>Current Status</span>
          <span
            style={{
              background: statusStyles[currentStatus]?.bg,
              color: statusStyles[currentStatus]?.color,
              border: `1px solid ${statusStyles[currentStatus]?.border}`,
              padding: "3px 16px",
              borderRadius: 20,
              fontWeight: 700,
              fontSize: "14px",
              letterSpacing: "1px",
            }}
          >
            {currentStatus.toUpperCase()}
          </span>
        </div>
      </div>

      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 40 }}>
      <div style={{ position: "absolute", left: 178, top: -30, bottom: -32, width: 1, background: "#eaeeed", zIndex: 0 }}/>
      {timeline.map((entry, index) => (
          
        <div key={entry.id} style={{ display: "flex", alignItems: "center", gap: 30, position: "relative" }}>
          <div
              style={{
                background: "#E9F5EB",
                padding: "8px 12px",
                borderRadius: 12,
                minWidth: 140,
                display: "flex",
                gap: 8,
                alignItems: "center",
                fontWeight: "500",
                fontSize: "15px",
                color: "#183930",
              }}
            >
              <CalendarIcon />
              {entry.date}
            </div>
           
           {(() => {
    const label = getStatusLabel(entry.status);

    const style = statusStyles[label] || statusStyles["Investment"];
    const dot   = dotStyles[label]   || dotStyles["Investment"];

    return (
      <div
        style={{
          position: "absolute",
          left: 167,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: dot.outerBg,
          border: `1px solid ${dot.outerBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: dot.innerBg,
          }}
        />
      </div>
      );
    })()}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: index * 0.2, 
              }}
              onClick={() => !loadingId && handleOpen(entry)}
              style={{
                flex: 1,
                marginLeft: 40, 
                border: "1px solid #eaeeed",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                justifyContent: "flex-start",
                //gap: 180,
                alignItems: "center",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
                cursor: loadingId === entry.id ? "wait" : "pointer",
                opacity: loadingId === entry.id ? 0.4 : 1,
              }}
            >
              {(() => {
              const label = getStatusLabel(entry.status);
              const dot = dotStyles[label];

              return (
                <div
                  style={{
                    width: 38,
                    height: 40,
                    borderRadius: 8,
                    background: dot.outerBg,
                    border: `1px solid ${dot.outerBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                {(() => {
                  const title = entry.title?.toLowerCase() || "";

                  if (title.includes("investment")) {
                    return <GoBriefcase size={18} style={{ color: dot.innerBg }} />;
                  }

                  if (title.includes("purchase")) {
                    return <FaArrowTrendUp size={18} style={{ color: dot.innerBg }} />;
                  }

                  if (title.includes("sale")) {
                    return <FaArrowTrendDown size={18} style={{ color: dot.innerBg }} />;
                  }

                  return (
                    <span
                      style={{
                        fontSize: 25,
                        color: dot.innerBg,
                        lineHeight: 1,
                        marginTop: "-2px",
                      }}
                    >
                      +
                    </span>
                  );
                 })()}
                </div>
              );
            })()}
              <div style={{ width: 10 }} />
              <div style={{ width: "45%" }}>

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  
                <span style={{ fontWeight: 600 }}>
                  {entry.title}
                </span>

                <span
                  style={{
                    color: "#B0C1BD",   
                    fontSize: 14,
                  }}
                >
                  •
                </span>

                <span
                  style={{
                    color: "#111",
                    fontSize: 14,
                    fontWeight: 400,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                  }}
                >
                  {entry.tranch}
                </span>
              </div>
                {(() => {
                  const statusLabel = getStatusLabel(entry.status).toLowerCase();

                  if (entry.isSale) {
                    const soldShares = Number(entry.noOfSharesSold || (entry.sharesFrom != null ? Math.max(0, entry.sharesFrom - entry.sharesTo) : 0));
                    return (
                      <div style={{ color: "#4D6D64", fontSize: 14 }}>
                        Sold {soldShares.toLocaleString()} shares for{" "}
                        <span style={{ fontWeight: 700 }}>
                          ₹ {Number(entry.totalSaleProceeds || 0).toLocaleString("en-IN")}
                        </span>{" "}
                        becomes {statusLabel}
                      </div>
                    );
                  }

                  if (entry.isBuyback) {
                    return (
                      <div style={{ color: "#4D6D64", fontSize: 14 }}>
                        Bought back {Number(entry.noOfSharesBoughtBack || 0).toLocaleString()} shares for{" "}
                        <span style={{ fontWeight: 700 }}>
                          ₹ {Number(entry.totalBuybackProceeds || 0).toLocaleString("en-IN")}
                        </span>{" "}
                        becomes {statusLabel}
                      </div>
                    );
                  }

                  if (entry.isBonus) {
                    return (
                      <div style={{ color: "#4D6D64", fontSize: 14 }}>
                        Received {Number(entry.bonusSharesReceived || 0).toLocaleString()} bonus shares becomes {statusLabel}
                      </div>
                    );
                  }

                  if (entry.isRights) {
                    return (
                      <div style={{ color: "#4D6D64", fontSize: 14 }}>
                        Subscribed {Number(entry.rightsExercised || 0).toLocaleString()} shares for{" "}
                        <span style={{ fontWeight: 700 }}>
                          ₹ {Number(entry.amountPaid || 0).toLocaleString("en-IN")}
                        </span>{" "}
                        becomes {statusLabel}
                      </div>
                    );
                  }

                  if ((entry.title || "").toLowerCase().includes("split")) {
                    return (
                      <div style={{ color: "#4D6D64", fontSize: 14 }}>
                        Stock split (Ratio: {entry.splitRatio || "N/A"}) becomes {statusLabel}
                      </div>
                    );
                  }

                  const purchasedShares = entry.noOfSharesPurchased || (
                    entry.sharesFrom === null
                      ? entry.sharesTo
                      : Math.max(0, entry.sharesTo - entry.sharesFrom)
                  );

                  return (
                    <div style={{ color: "#4D6D64", fontSize: 14 }}>
                      Purchased {Number(purchasedShares || 0).toLocaleString()} shares for{" "}
                      <span style={{ fontWeight: 700 }}>
                        ₹ {Number(entry.totalConsideration || 0).toLocaleString("en-IN")}
                      </span>{" "}
                      becomes {statusLabel}
                    </div>
                  );
                })()}
              </div>

             <div style={{ width: "20%", textAlign: "left" }}>
                <div style={{ fontSize: 13, color: "#4D6D64", textAlign: "left" }}>
                  Shares
                </div>
                {entry.sharesFrom !== null && entry.sharesFrom !== undefined ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    {entry.sharesFrom.toLocaleString()}
                    <ArrowRightIcon style={{ marginTop: 4 }}  />
                    <div style= {{ color: entry.sharesTo < entry.sharesFrom ? "#E5484D" : "#349F6F", fontSize: "16px", fontWeight: "700"}}>{entry.sharesTo.toLocaleString()}</div>
                  </div>
                ) : (
                  <b>{entry.sharesTo.toLocaleString()}</b>
                )}
              </div>

              
                 <div style={{ width: "15%", textAlign: "left" }}>
                  <div style={{ fontSize: 13, color: "#4D6D64", textAlign: "left" }}>
                    Ownership
                  </div>
                <b>{entry.ownership} %</b>
              </div>

             {(() => {
              const label = getStatusLabel(entry.status);
              return (
                <div
                  style={{
                    background: statusStyles[label]?.bg,
                    color: statusStyles[label]?.color,
                    border: `1px solid ${statusStyles[label]?.border}`,
                    padding: "3px 14px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 600,
                    textWrap: "nowrap",
                  }}
                >
                  {label}
                </div>
              );
            })()}
            <div
            onClick={() => !loadingId && handleOpen(entry)}
            style={{
              marginLeft: "auto",
              cursor: loadingId === entry.id ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              opacity: loadingId === entry.id ? 0.4 : 1,
            }}
          >
            <RightArrow width={18} height={18} />
          </div>
            </motion.div>
          </div>
        ))}
       </div>
    </div>
     {openEntry && (
      <CreateInvestment2
        transactionID={openEntry.id}
        investmentID={openEntry.investmentID}
        tranch={openEntry.tranch}
        initialData={entryData}
        timelineData={timelineData?.timeline}
        onBack={handleBack}
      />
    )}
    </div>
  );
};

export default InvestmentTimeline;